import React, { useEffect, useMemo, useState } from "react";
import { PERSONAL_DETAILS, FORM_QUESTIONS, CONGRATS_TEXT } from "../data/questions";
import QuestionRenderer from "../components/QuestionRenderer";
import BrandHeader from "../components/BrandHeader";
import { submitFeedback } from "../Api/api";
import img from '../../assets/backtgch.png';
import { Box, Button, Card, CardContent, Container, Typography } from "@mui/material";

export default function FeedbackForm() {
  const [step, setStep] = useState(0); // 0 personal, 1 questions
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const base = step === 0 ? PERSONAL_DETAILS : FORM_QUESTIONS;

  const visible = useMemo(() => {
    return base.filter((q) => (q.showIf ? q.showIf(answers) : true));
  }, [base, answers]);

  // remove hidden answers + hidden errors
  useEffect(() => {
    const visibleIds = new Set(visible.map((q) => q.id));

    setAnswers((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        // keep personal in step 1 cleanup
        if (PERSONAL_DETAILS.some((p) => p.id === k)) return;
        if (step === 1 && !visibleIds.has(k)) delete next[k];
      });
      return next;
    });

    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (PERSONAL_DETAILS.some((p) => p.id === k)) return;
        if (step === 1 && !visibleIds.has(k)) delete next[k];
      });
      return next;
    });
  }, [visible, step]);

  const onChange = (id, value) => {
    setAnswers((p) => ({ ...p, [id]: value }));
    setErrors((p) => ({ ...p, [id]: "" }));
  };

  // ✅ Validation rules:
  // - Validate only visible
  // - Only required fields
  // - BUT: text/message fields are NOT required (your rule)
  const validateList = (list) => {
    const nextErrors = {};
    for (const q of list) {
      if (!q.required) continue;
      if (q.type === "text" || q.type === "message") continue;

      const v = answers[q.id];
      if (v === undefined || v === null || String(v).trim() === "") {
        nextErrors[q.id] = "This field is required";
      }
    }
    return nextErrors;
  };

  const validatePersonalTextRequired = () => {
    const nextErrors = {};
    for (const q of PERSONAL_DETAILS) {
      if (!q.required) continue;
      if (q.type !== "text" && q.type !== "email") continue;

      const v = answers[q.id];
      if (!v || String(v).trim() === "") nextErrors[q.id] = "This field is required";
    }
    return nextErrors;
  };

  const nextPage = () => {
    const errs1 = validateList(PERSONAL_DETAILS);
    const errs2 = validatePersonalTextRequired();
    const errs = { ...errs1, ...errs2 };

    if (Object.keys(errs).length) {
      setErrors(errs);
      alert("Please fill all required personal details.");
      return;
    }
    setErrors({});
    setStep(1);
  };

  const submit = async () => {
    const errs = validateList(visible);
    if (Object.keys(errs).length) {
      setErrors(errs);
      alert("Please fill all required questions.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        email: answers.email,
        name: answers.name,
        answers: [
          // personal
          ...PERSONAL_DETAILS.filter((q) => !["email", "name"].includes(q.id)).map((q) => ({
            question_id: q.id,
            value: answers[q.id] ?? "",
          })),
          // step 2 (only visible)
          ...visible.map((q) => ({
            question_id: q.id,
            value: answers[q.id] ?? "",
          })),
        ],
      };

      await submitFeedback(payload);

      alert("Submitted successfully ✅");
      setDone(true);
    } catch (e) {
      alert("Submit failed: " + (e?.message || ""));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setAnswers({});
    setErrors({});
    setStep(0);
    setDone(false);
    setSubmitting(false);
  };

  return (
   <Box
  sx={{
    minHeight: "100vh",
    backgroundImage: `url(${img})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    py: 4,
  }}
>

      <BrandHeader />

      <Container maxWidth="md">
        <Card sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            {!done ? (
              <>
                <Typography variant="h5" fontWeight={900} mb={2}>
                  {step === 0 ? "Personal Details" : "Questions"}
                </Typography>

                {visible.map((q) => (
                  <QuestionRenderer
                    key={q.id}
                    q={q}
                    value={answers[q.id]}
                    onChange={onChange}
                    errorText={errors[q.id]}
                  />
                ))}

                <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
                  {step === 1 && (
                    <Button variant="outlined" onClick={() => setStep(0)} disabled={submitting}>
                      Back
                    </Button>
                  )}

                  {step === 0 ? (
                    <Button variant="contained" onClick={nextPage} disabled={submitting}>
                      Next
                    </Button>
                  ) : (
                    <Button variant="contained" onClick={submit} disabled={submitting}>
                      {submitting ? "Submitting..." : "Submit"}
                    </Button>
                  )}
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h4" fontWeight={900} mb={2}>
                  🎉 Congratulations!
                </Typography>

                <Typography sx={{ whiteSpace: "pre-line", mb: 4 }}>
                  {CONGRATS_TEXT}
                </Typography>

                <Button variant="contained" onClick={resetForm}>
                  Submit Another Response
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
